export const palette = [
  {
    name: 'Bleu marine profond',
    hex: '#092653',
    role: 'Couleur principale — confiance, solidité',
    usage: 'Fonds structurants, navigation, titres, boutons, documents',
    dotClass: 'bg-primary-500',
    textClass: 'text-background-50',
    share: '55 %',
    token: 'primary-500',
  },
  {
    name: 'Orange industriel',
    hex: '#F47721',
    role: 'Couleur d’accentuation — énergie, action',
    usage: 'Appels à l’action, repères de sécurité, marquages, chiffres clés',
    dotClass: 'bg-accent-500',
    textClass: 'text-foreground-950',
    share: '15 %',
    token: 'accent-500',
  },
  {
    name: 'Gris métallique',
    hex: '#87939F',
    role: 'Couleur de soutien — acier, équipements',
    usage: 'Filets, textes secondaires, fonds techniques, icônes',
    dotClass: 'bg-secondary-500',
    textClass: 'text-background-50',
    share: '10 %',
    token: 'secondary-500',
  },
  {
    name: 'Blanc',
    hex: '#FFFFFF',
    role: 'Couleur de respiration — clarté',
    usage: 'Fonds de page, cartes, réserves autour du logo',
    dotClass: 'bg-background-50 ring-1 ring-inset ring-background-300',
    textClass: 'text-foreground-950',
    share: '15 %',
    token: 'background-50',
  },
  {
    name: 'Noir anthracite',
    hex: '#17191D',
    role: 'Couleur de contraste — textes et fonds sombres',
    usage: 'Textes courants, pied de page, sections denses',
    dotClass: 'bg-foreground-950',
    textClass: 'text-background-50',
    share: '5 %',
    token: 'foreground-950',
  },
];

export const logoRules = [
  {
    title: 'Zone de protection',
    text: 'Autour du logo, réservez un espace libre égal à la hauteur du « S ». Aucun texte, filet ou photo ne pénètre cette zone.',
    icon: 'ri-square-line',
  },
  {
    title: 'Taille minimale',
    text: '48 px de hauteur à l’écran, 18 mm sur un document imprimé. En dessous, utilisez la version compacte.',
    icon: 'ri-ruler-2-line',
  },
  {
    title: 'Fonds autorisés',
    text: 'Le logo s’utilise sur blanc, sur bleu marine et sur gris métallique clair. Jamais sur une photo non traitée.',
    icon: 'ri-contrast-drop-2-line',
  },
  {
    title: 'À ne jamais faire',
    text: 'Pas de déformation, pas de rotation, pas de changement de couleur, pas d’ombre portée, pas de contour ajouté.',
    icon: 'ri-forbid-2-line',
  },
];

export const typographySpecimens = [
  {
    name: 'Barlow Condensed',
    role: 'Titres & affichage',
    weights: '500 · 600 · 700',
    sample: 'Bâtisseurs de l’industrie',
    className: 'font-heading uppercase font-semibold',
    detail:
      'Condensée, droite, en majuscules pour les titres. Elle donne la puissance industrielle sans agressivité.',
  },
  {
    name: 'Inter',
    role: 'Textes, interfaces & boutons',
    weights: '400 · 500 · 600',
    sample: 'Chaudronnerie, tuyauterie et soudure certifiée au Cameroun.',
    className: 'font-body',
    detail:
      'Très lisible en petit corps, y compris sur mobile. Elle porte les paragraphes, les formulaires et les libellés techniques.',
  },
];

export type SupportKind =
  | 'letterhead'
  | 'business-card'
  | 'quote'
  | 'signature'
  | 'workwear'
  | 'helmet'
  | 'vehicle'
  | 'site-sign'
  | 'document-cover'
  | 'social';

export const brandSupports: { kind: SupportKind; title: string; text: string; tag: string }[] = [
  {
    kind: 'letterhead',
    title: 'Papier à en-tête',
    text: 'Bandeau marine en tête, logo en réserve blanche, filet orange de 2 mm, mention légale en pied de page.',
    tag: 'Papeterie',
  },
  {
    kind: 'business-card',
    title: 'Carte de visite',
    text: 'Recto marine avec le logo centré, verso blanc : nom en Barlow Condensed, fonction, contacts en Inter.',
    tag: 'Papeterie',
  },
  {
    kind: 'quote',
    title: 'Devis & facture',
    text: 'Grille de prix sobre, bandeau marine, total encadré en orange. Référence, date et mentions de validité clairement isolées.',
    tag: 'Commercial',
  },
  {
    kind: 'signature',
    title: 'Signature e-mail',
    text: 'Nom, fonction, ligne orange de séparation, coordonnées et lien vers le site. Aucune image lourde.',
    tag: 'Numérique',
  },
  {
    kind: 'workwear',
    title: 'Tenues de travail',
    text: 'Combinaison marine, bandes de visibilité orange, logo brodé poitrine gauche et au dos.',
    tag: 'Terrain',
  },
  {
    kind: 'helmet',
    title: 'Casques de sécurité',
    text: 'Casque blanc pour l’encadrement, orange pour les équipes. Logo en sticker résistant sur la face avant.',
    tag: 'Terrain',
  },
  {
    kind: 'vehicle',
    title: 'Marquage des véhicules',
    text: 'Caisse blanche, bande marine basse, logo sur les portes avant et numéro d’atelier en orange.',
    tag: 'Flotte',
  },
  {
    kind: 'site-sign',
    title: 'Panneaux de chantier',
    text: 'Panneau marine à liseré orange : logo, intitulé du chantier, maître d’ouvrage et consignes de sécurité.',
    tag: 'Chantier',
  },
  {
    kind: 'document-cover',
    title: 'Couvertures de documents',
    text: 'Couverture A4 marine avec trame technique discrète, logo en haut, titre du dossier en bas.',
    tag: 'Édition',
  },
  {
    kind: 'social',
    title: 'Réseaux sociaux',
    text: 'Publications carrées : photo de chantier, bandeau marine translucide, titre en Barlow Condensed, repère orange.',
    tag: 'Numérique',
  },
];

export const graphicUnivers = [
  {
    icon: 'ri-ruler-line',
    title: 'Lignes techniques',
    text: 'Filets de 1 px, cotes et repères de plan rappellent le bureau d’études. Ils structurent sans décorer.',
  },
  {
    icon: 'ri-shape-2-line',
    title: 'Géométrie constructive',
    text: 'Angles droits, rectangles, diagonales à 45° : la grammaire de la charpente métallique.',
  },
  {
    icon: 'ri-layout-grid-line',
    title: 'Trames discrètes',
    text: 'Grille de plan ou hachures à 135°, toujours sous 10 % d’opacité, pour donner de la matière aux fonds.',
  },
  {
    icon: 'ri-image-line',
    title: 'Photographie',
    text: 'Soudure, chaudronnerie, tuyauterie, structures, équipes en intervention : des images réelles, contrastées, sans pose.',
  },
];